import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Mail, CheckCircle2, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Reveal, SectionHeading } from "../components/Reveal";
import { PHONE_DISPLAY, PHONE_TEL, EMAIL, EMAIL_MAILTO } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUBJECTS = [
  "General enquiry",
  "Skip enquiry — 8 Cubic Yard",
  "Skip enquiry — 14 Cubic Yard",
  "Skip enquiry — 25 Roll-on",
  "Skip enquiry — 40 Roll-on",
  "Permit question",
  "Other",
];

const inputCls =
  "w-full rounded-xl border border-ink/15 bg-white px-4 py-3.5 text-sm text-ink placeholder:text-ink/35 outline-none transition-[border-color,box-shadow] duration-300 focus:border-emerald-700 focus:ring-2 focus:ring-emerald-700/15";

const labelCls = "mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-ink/60";

export default function ContactPage() {
  const [params] = useSearchParams();
  const incoming = params.get("subject");
  const subjects =
    incoming && !SUBJECTS.includes(incoming) ? [incoming, ...SUBJECTS] : SUBJECTS;

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: incoming || "General enquiry",
    message: "",
  });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/contact`, form);
      setDone(data);
      toast.success("Enquiry sent — we'll be in touch shortly.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : `Could not send your message — please call us on ${PHONE_DISPLAY}.`
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="mx-auto max-w-6xl px-5 py-16 sm:px-8 sm:py-20">
      <SectionHeading
        eyebrow="Contact"
        title="Talk to the crew, not a call centre."
        sub="Mon–Sat, 7am–7pm · Delivering across Dublin & Kildare."
      />

      <div className="grid gap-5 lg:grid-cols-2">
        <Reveal>
          <a
            href={PHONE_TEL}
            data-testid="contact-phone-link"
            className="group block h-full rounded-2xl border border-ink/10 bg-white p-8 transition-[border-color,box-shadow] duration-300 hover:border-ink/25 hover:shadow-[0_16px_40px_rgba(0,0,0,0.06)]"
          >
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-700/10">
              <Phone className="h-5 w-5 text-emerald-700" />
            </div>
            <p className="mt-5 font-mono text-[11px] tracking-[0.25em] text-ink/50">CALL US</p>
            <p className="mt-1.5 font-heading text-2xl font-extrabold text-ink transition-colors duration-300 group-hover:text-emerald-700 sm:text-3xl">
              {PHONE_DISPLAY}
            </p>
          </a>
        </Reveal>
        <Reveal delay={0.08}>
          <a
            href={EMAIL_MAILTO}
            data-testid="contact-email-link"
            className="group block h-full rounded-2xl border border-ink/10 bg-white p-8 transition-[border-color,box-shadow] duration-300 hover:border-ink/25 hover:shadow-[0_16px_40px_rgba(0,0,0,0.06)]"
          >
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-700/10">
              <Mail className="h-5 w-5 text-emerald-700" />
            </div>
            <p className="mt-5 font-mono text-[11px] tracking-[0.25em] text-ink/50">EMAIL US</p>
            <p className="mt-1.5 break-all font-heading text-xl font-extrabold text-ink transition-colors duration-300 group-hover:text-emerald-700 sm:text-2xl">
              {EMAIL}
            </p>
          </a>
        </Reveal>
      </div>

      <div className="mt-16 max-w-2xl">
        <SectionHeading
          eyebrow="Send a message"
          title="Enquiry form."
          sub="Quotes for the 8, 14 or roll-on skips, permits, anything else — send it over and we'll reply within the hour."
        />
        <Reveal>
          <div className="rounded-2xl border border-ink/10 bg-white p-6 shadow-[0_16px_50px_rgba(0,0,0,0.05)] sm:p-10">
            <AnimatePresence mode="wait">
              {done ? (
                <motion.div
                  key="done"
                  initial={{ opacity: 0, scale: 0.97 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                  className="py-6 text-center"
                  data-testid="contact-confirmation"
                >
                  <CheckCircle2 className="mx-auto h-14 w-14 text-emerald-700" />
                  <h3 className="mt-5 font-heading text-2xl font-extrabold text-ink">
                    Message received
                  </h3>
                  <p className="mt-2 font-mono text-sm tracking-[0.2em] text-emerald-700" data-testid="contact-reference">
                    REF · {done.reference}
                  </p>
                  <p className="mx-auto mt-5 max-w-sm text-sm leading-relaxed text-ink/65">
                    We've emailed you a copy and we'll reply within the hour (Mon–Sat, 7am–7pm).
                  </p>
                  <button
                    data-testid="contact-another-button"
                    onClick={() => {
                      setForm({ name: "", email: "", phone: "", subject: "General enquiry", message: "" });
                      setDone(null);
                    }}
                    className="mt-7 rounded-full border border-ink/20 px-6 py-3 font-heading text-sm font-bold text-ink transition-colors duration-300 hover:bg-ink hover:text-white"
                  >
                    Send another message
                  </button>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.3 }}
                  onSubmit={submit}
                  className="space-y-5"
                  data-testid="contact-form"
                >
                  <div>
                    <label className={labelCls} htmlFor="ct-subject">What's it about?</label>
                    <select
                      id="ct-subject"
                      data-testid="contact-form-subject-select"
                      required
                      value={form.subject}
                      onChange={set("subject")}
                      className={`${inputCls} appearance-none`}
                    >
                      {subjects.map((s) => (
                        <option key={s} value={s} className="bg-white text-ink">
                          {s}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="grid gap-5 sm:grid-cols-2">
                    <div>
                      <label className={labelCls} htmlFor="ct-name">Full name</label>
                      <input
                        id="ct-name"
                        data-testid="contact-form-name-input"
                        required
                        value={form.name}
                        onChange={set("name")}
                        placeholder="Mary Murphy"
                        className={inputCls}
                      />
                    </div>
                    <div>
                      <label className={labelCls} htmlFor="ct-phone">Phone (optional)</label>
                      <input
                        id="ct-phone"
                        data-testid="contact-form-phone-input"
                        type="tel"
                        value={form.phone}
                        onChange={set("phone")}
                        placeholder="087 123 4567"
                        className={inputCls}
                      />
                    </div>
                  </div>

                  <div>
                    <label className={labelCls} htmlFor="ct-email">Email</label>
                    <input
                      id="ct-email"
                      data-testid="contact-form-email-input"
                      required
                      type="email"
                      value={form.email}
                      onChange={set("email")}
                      placeholder="you@example.ie"
                      className={inputCls}
                    />
                  </div>

                  <div>
                    <label className={labelCls} htmlFor="ct-message">Message</label>
                    <textarea
                      id="ct-message"
                      data-testid="contact-form-message-input"
                      required
                      rows={5}
                      value={form.message}
                      onChange={set("message")}
                      placeholder="Hi — I'm after a price for the 25 roll-on for a job in Naas starting next month…"
                      className={`${inputCls} resize-none`}
                    />
                  </div>

                  <button
                    type="submit"
                    data-testid="contact-form-submit-button"
                    disabled={sending}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-ink py-4 font-heading text-base font-bold text-white transition-colors duration-300 hover:bg-emerald-800 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {sending ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin" />
                        Sending…
                      </>
                    ) : (
                      <>
                        Send Enquiry
                        <ArrowRight className="h-5 w-5" />
                      </>
                    )}
                  </button>
                </motion.form>
              )}
            </AnimatePresence>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
